import React, { useMemo, useState, useLayoutEffect } from "react";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

function startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}
function endOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
}
function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function addMonths(d: Date, n: number): Date {
    const x = new Date(d);
    x.setMonth(x.getMonth() + n);
    return x;
}
function formatHour(h: number): string {
    const period = h < 12 ? "AM" : "PM";
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${display} ${period}`;
}
function formatRangeLabel(start: Date, end: Date): string {
    const s = `${start.getDate()} ${MONTHS[start.getMonth()].slice(0, 3)}`;
    const e = `${end.getDate()} ${MONTHS[end.getMonth()].slice(0, 3)}`;
    const ts = formatHour(start.getHours());
    const te = formatHour(end.getHours());
    if (sameDay(start, end)) return `${s} · ${ts} – ${te}`;
    return `${s} ${ts} → ${e} ${te}`;
}

/* ============================================================
   AnalogClock — hour-only selection via click
   ============================================================ */
function AnalogClock({
    label,
    hour,
    onPick,
    accent,
}: {
    label: string;
    hour: number;
    onPick: (h: number) => void;
    accent: boolean;
}) {
    const [hoverHour, setHoverHour] = useState<number | null>(null);
    const size = 160;
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 16;

    const hourToAngle = (h: number) => {
        // 12 at top, going clockwise
        const norm = h % 12;
        return (norm * 30 - 90) * (Math.PI / 180);
    };

    const hourPositions = Array.from({ length: 12 }, (_, i) => {
        const h = i === 0 ? 12 : i;
        const angle = hourToAngle(h);
        return {
            hour: h,
            x: cx + r * Math.cos(angle),
            y: cy + r * Math.sin(angle),
        };
    });

    const handAngle = hourToAngle(hour);
    const handX = cx + (r - 30) * Math.cos(handAngle);
    const handY = cy + (r - 30) * Math.sin(handAngle);

    // The AM/PM period must come from the committed hour only. Deriving it
    // from the hovered hour made the period buttons and the hour-click
    // resolution flip between AM and PM just by moving the mouse (and was a
    // race on touch devices).
    const isPM = hour >= 12;
    const resolveHour = (h: number): number =>
        isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);

    const displayHour = hoverHour !== null ? resolveHour(hoverHour) : hour;

    return (
        <div className={`clock ${accent ? "clock-accent" : ""}`}>
            <div className="clock-label">{label}</div>
            <div className="clock-period">
                <button
                    className={`period-btn ${!isPM ? "active" : ""}`}
                    onClick={() => onPick(hour % 12)}
                >AM</button>
                <button
                    className={`period-btn ${isPM ? "active" : ""}`}
                    onClick={() => onPick((hour % 12) + 12)}
                >PM</button>
            </div>
            <svg width={size} height={size} className="clock-face">
                <defs>
                    <radialGradient id={`clockBg-${label}`}>
                        <stop offset="0%" stopColor="var(--bg-3)" />
                        <stop offset="100%" stopColor="var(--bg-2)" />
                    </radialGradient>
                </defs>
                <circle cx={cx} cy={cy} r={r + 8} fill={`url(#clockBg-${label})`} stroke="var(--border-strong)" strokeWidth="1" />
                {/* hour markers */}
                {hourPositions.map(({ hour: h, x, y }) => {
                    const isActive = hoverHour === h || (hoverHour === null && (hour % 12 === h % 12));
                    return (
                        <g key={h}>
                            <circle
                                cx={x} cy={y} r="16"
                                fill={isActive ? "var(--accent)" : "var(--bg-elev)"}
                                stroke={isActive ? "var(--accent)" : "var(--border)"}
                                strokeWidth="1"
                                className="clock-hour-bg"
                            />
                            <text
                                x={x} y={y}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fill={isActive ? "var(--bg-0)" : "var(--text-1)"}
                                fontSize="13"
                                fontWeight={isActive ? "800" : "600"}
                                className="clock-hour-text"
                            >{h}</text>
                            {/* invisible larger hit area */}
                            <circle
                                cx={x} cy={y} r="22"
                                fill="transparent"
                                className="clock-hour-hit"
                                onMouseEnter={() => setHoverHour(h)}
                                onMouseLeave={() => setHoverHour(null)}
                                onClick={() => onPick(resolveHour(h))}
                            />
                        </g>
                    );
                })}
                {/* center dot */}
                <circle cx={cx} cy={cy} r="4" fill="var(--accent)" />
                {/* hour hand */}
                <line
                    x1={cx} y1={cy} x2={handX} y2={handY}
                    stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
                    className="clock-hand"
                />
            </svg>
            <div className="clock-readout">{formatHour(hour)}</div>
        </div>
    );
}

/* ============================================================
   DateRangePicker
   ============================================================ */
type Props = {
    minDate: Date;
    maxDate: Date;
    startDate: Date;
    endDate: Date;
    onChange: (start: Date, end: Date) => void;
};

export function DateRangePicker({ minDate, maxDate, startDate, endDate, onChange }: Props) {
    const [viewMonth, setViewMonth] = useState<Date>(startOfDay(startDate));
    const [phase, setPhase] = useState<"start" | "end">("start");
    const [hover, setHover] = useState<Date | null>(null);
    const pendingStart = React.useRef<Date | null>(null);
    const isPicking = React.useRef(false);
    const phaseRef = React.useRef(phase);
    phaseRef.current = phase;
    const hoverRef = React.useRef(hover);
    hoverRef.current = hover;
    const [wiggleKey, setWiggleKey] = useState<number>(0);
    const [wiggleDate, setWiggleDate] = useState<Date | null>(null);

    // Sync to parent-committed range only when NOT actively picking
    const keyRef = React.useRef<string>("");
    React.useLayoutEffect(() => {
        const k = `${startDate.getTime()}-${endDate.getTime()}`;
        if (k !== keyRef.current && !isPicking.current) {
            keyRef.current = k;
            pendingStart.current = null;
            if (phaseRef.current !== "start" || hoverRef.current !== null) {
                setPhase("start");
                setHover(null);
            }
        }
    }, [startDate, endDate]);

    const min = startOfDay(minDate);
    const max = endOfDay(maxDate);

    const canGoPrev = useMemo(() => {
        const prev = addMonths(viewMonth, -1);
        return prev >= new Date(min.getFullYear(), min.getMonth(), 1);
    }, [viewMonth, min]);
    const canGoNext = useMemo(() => {
        const next = addMonths(viewMonth, 1);
        return next <= new Date(max.getFullYear(), max.getMonth() + 1, 0);
    }, [viewMonth, max]);

    const days = useMemo(() => {
        const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
        const startOffset = first.getDay();
        const gridStart = new Date(first);
        gridStart.setDate(first.getDate() - startOffset);
        const cells: Date[] = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(gridStart);
            d.setDate(gridStart.getDate() + i);
            cells.push(d);
        }
        return cells;
    }, [viewMonth]);

    const isDisabled = (d: Date): boolean => {
        const day = startOfDay(d);
        return day < min || day > max;
    };
    const isToday = (d: Date): boolean => sameDay(d, new Date());

    const activeStart = startDate;
    const activeEnd = phase === "end" && hover ? hover : endDate;
    const rangeStart = activeStart <= activeEnd ? activeStart : activeEnd;
    const rangeEnd = activeStart <= activeEnd ? activeEnd : activeStart;

    const inRange = (d: Date): boolean => {
        const day = startOfDay(d);
        const rs = startOfDay(rangeStart);
        const re = startOfDay(rangeEnd);
        return day > rs && day < re;
    };
    const isRangeStart = (d: Date): boolean => sameDay(d, rangeStart);
    const isRangeEnd = (d: Date): boolean => sameDay(d, rangeEnd);
    const isRangeEdge = (d: Date): boolean => isRangeStart(d) || isRangeEnd(d);

    const isHoverPreview = (d: Date): boolean => {
        if (phase !== "end" || !hover) return false;
        const day = startOfDay(d);
        const hs = startOfDay(pendingStart.current!);
        const he = startOfDay(hover);
        const dayStart = hs <= he ? hs : he;
        const dayEnd = hs <= he ? he : hs;
        return day > dayStart && day <= dayEnd;
    };

    const triggerWiggle = (d: Date) => {
        setWiggleKey(k => k + 1);
        setWiggleDate(new Date(d));
    };

    const handleDayClick = (d: Date) => {
        if (isDisabled(d)) return;
        const day = startOfDay(d);
        triggerWiggle(day);
        if (phase === "start") {
            isPicking.current = true;
            pendingStart.current = day;
            day.setHours(startDate.getHours(), 0, 0, 0);
            onChange(day, endDate < day ? day : endDate);
            setPhase("end");
            setHover(day);
        } else {
            isPicking.current = false;
            let s = new Date(pendingStart.current!);
            s.setHours(startDate.getHours(), 0, 0, 0);
            let e = day;
            e.setHours(endDate.getHours(), 59, 59, 999);
            if (e < s) { [s, e] = [e, s]; }
            if (sameDay(s, e) && e.getHours() <= s.getHours()) {
                e.setHours(s.getHours() + 1, 59, 59, 999);
            }
            onChange(s, e);
            pendingStart.current = null;
            setPhase("start");
            setHover(null);
        }
    };

    const handleStartHour = (h: number) => {
        const s = new Date(startDate);
        s.setHours(h, 0, 0, 0);
        let e = new Date(endDate);
        if (e <= s) {
            e = new Date(s);
            e.setHours(s.getHours() + 1, 59, 59, 999);
        }
        onChange(s, e);
    };

    const handleEndHour = (h: number) => {
        let e = new Date(endDate);
        e.setHours(h, 59, 59, 999);
        const s = new Date(startDate);
        if (sameDay(s, e) && e <= s) {
            // An end hour before the start hour means an overnight range:
            // keep the start as chosen and push the end to the next day.
            e.setDate(e.getDate() + 1);
        }
        onChange(s, e);
    };

    const presets: { label: string; fn: () => void }[] = [
        { label: "Full Range", fn: () => onChange(new Date(min), new Date(max)) },
        {
            label: "Today",
            fn: () => {
                const t = startOfDay(new Date());
                const te = endOfDay(new Date());
                onChange(t, te);
            },
        },
    ];

    const stepMonth = (dir: number) => {
        if (dir < 0 && !canGoPrev) return;
        if (dir > 0 && !canGoNext) return;
        setViewMonth(m => addMonths(m, dir));
    };

    return (
        <div className="drp">
            <div className="drp-header">
                <div className="drp-range-label">{formatRangeLabel(startDate, endDate)}</div>
            </div>

            <div className="drp-presets">
                {presets.map(p => (
                    <button key={p.label} className="drp-preset" onClick={p.fn}>{p.label}</button>
                ))}
            </div>

            <div className="drp-body">
                <div className="drp-calendar">
                    <div className="drp-cal-nav">
                        <button className="drp-nav-btn" disabled={!canGoPrev} onClick={() => stepMonth(-1)}>‹</button>
                        <span className="drp-cal-title">{MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
                        <button className="drp-nav-btn" disabled={!canGoNext} onClick={() => stepMonth(1)}>›</button>
                    </div>
                    <div className="drp-weekdays">
                        {WEEKDAYS.map(w => <div key={w} className="drp-weekday">{w}</div>)}
                    </div>
                    <div className="drp-grid"
                         onMouseLeave={() => phase === "end" && setHover(null)}
                    >
                        {days.map((d, i) => {
                            const disabled = isDisabled(d);
                            const edge = isRangeEdge(d);
                            const between = inRange(d);
                            const isSelected = edge || between;
                            const outside = d.getMonth() !== viewMonth.getMonth() && !isSelected;
                            const preview = isHoverPreview(d);
                            const today = isToday(d);
                            const isStart = isRangeStart(d);
                            const isEnd = isRangeEnd(d);
                            const shouldWiggle = wiggleDate && sameDay(wiggleDate, d);
                            const cls = [
                                "drp-day",
                                disabled ? "out-of-range" : "",
                                outside ? "outside" : "",
                                isStart ? "range-start" : "",
                                isEnd ? "range-end" : "",
                                between ? "between" : "",
                                preview ? "preview" : "",
                                today ? "today" : "",
                                shouldWiggle ? "wiggle" : "",
                            ].filter(Boolean).join(" ");
                            return (
                                <button
                                    key={`${i}-${shouldWiggle ? wiggleKey : "0"}`}
                                    className={cls}
                                    onClick={() => handleDayClick(d)}
                                    onMouseEnter={() => !disabled && phase === "end" && setHover(d)}
                                >
                                    {d.getDate()}
                                </button>
                            );
                        })}
                    </div>
                    <div className="drp-hint">
                        {phase === "start"
                            ? "Click a date to start your range"
                            : "Click another date to complete the range"}
                    </div>
                </div>

                <div className="drp-clocks">
                    <AnalogClock
                        label="Start"
                        hour={startDate.getHours()}
                        onPick={handleStartHour}
                        accent={phase === "start"}
                    />
                    <AnalogClock
                        label="End"
                        hour={endDate.getHours()}
                        onPick={handleEndHour}
                        accent={phase === "end"}
                    />
                </div>
            </div>
        </div>
    );
}
